import React, { useState, useEffect, useMemo } from 'react';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { pdfTemplatesApi, PdfTemplate } from '../api';
import { buildingsApi, Building, Operator } from '../api/buildingsApi';
import { TemplateEditor } from '../components/TemplateEditor';
import { getPdfTemplateTypeLabel } from '../utils/pdfTemplateTypes';
import {
  applyDefaultBuildingSelection,
  needsBuildingSelection,
  shouldShowBuildingSelector,
  selectBuilding,
} from '../utils/buildingSelection';
import {
  buildTemplateRows,
  getTemplateSourceLabel,
  type TemplateRow,
} from '../utils/pdfTemplateScope.util';

export const PdfTemplates: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const [templates, setTemplates] = useState<PdfTemplate[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PdfTemplate | null>(null);
  const [saveAsBuildingOverride, setSaveAsBuildingOverride] = useState(false);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [selectedOperatorScopeId, setSelectedOperatorScopeId] = useState('');

  const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId);
  const scopeOperatorId =
    user?.role === 'OPERATOR'
      ? user.id
      : user?.role === 'SUPERADMIN' && !selectedBuildingId && selectedOperatorScopeId
        ? selectedOperatorScopeId
        : null;

  const templateRows = useMemo(
    () =>
      buildTemplateRows(
        templates,
        selectedBuildingId,
        selectedBuilding?.operatorId ?? null,
        scopeOperatorId,
      ),
    [templates, selectedBuildingId, selectedBuilding?.operatorId, scopeOperatorId],
  );

  useEffect(() => {
    loadTemplates();
    loadBuildings();
    if (user?.role === 'SUPERADMIN') {
      buildingsApi.getOperators().then(setOperators).catch(console.error);
    }
  }, [user?.role]);

  useEffect(() => {
    if (user?.role !== 'SUPERADMIN' || !selectedOperatorScopeId) return;
    pdfTemplatesApi
      .repairOperatorScopes(selectedOperatorScopeId)
      .then(() => loadTemplates())
      .catch(console.error);
  }, [selectedOperatorScopeId, user?.role]);

  const loadBuildings = async () => {
    try {
      const data = await buildingsApi.getBuildings();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch (error) {
      console.error('Error loading buildings:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await pdfTemplatesApi.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error(t('errorLoadingTemplates'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = async (row: TemplateRow) => {
    const template = row.template;
    if (row.inherited) return;

    const confirmMessage = template.isDefault
      ? `${t('confirmDeleteTemplate')}\n${t('defaultTemplateWillBeReassigned')}`
      : t('confirmDeleteTemplate');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await pdfTemplatesApi.deleteTemplate(template.id);
      toast.success(t('templateDeletedSuccess'));
      loadTemplates();
    } catch (error: any) {
      console.error('Error deleting template:', error);
      toast.error(error.response?.data?.message || t('errorDeletingTemplate'));
    }
  };

  const handleResetBuildingTemplate = async (row: TemplateRow) => {
    if (!selectedBuildingId || row.inherited) return;
    if (!window.confirm('Xóa mẫu riêng của chung cư và dùng lại mẫu chủ đầu tư?')) {
      return;
    }
    try {
      await pdfTemplatesApi.resetBuildingDefault(
        selectedBuildingId,
        row.template.templateType,
      );
      toast.success('Đã dùng lại mẫu chủ đầu tư');
      loadTemplates();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errorUpdatingTemplate'));
    }
  };

  const handleSetDefault = async (template: PdfTemplate) => {
    try {
      await pdfTemplatesApi.setAsDefault(template.id);
      toast.success(t('templateSetAsDefaultSuccess'));
      loadTemplates();
    } catch (error: any) {
      console.error('Error setting default template:', error);
      toast.error(error.response?.data?.message || t('errorUpdatingTemplate'));
    }
  };

  const handleSeedDefaults = async () => {
    try {
      await pdfTemplatesApi.seedDefaultTemplates();
      toast.success(t('defaultTemplatesCreatedSuccess'));
      loadTemplates();
    } catch (error: any) {
      console.error('Error seeding defaults:', error);
      toast.error(error.response?.data?.message || t('errorCreatingTemplate'));
    }
  };

  const openEditModal = (row: TemplateRow) => {
    setEditingTemplate(row.template);
    setSaveAsBuildingOverride(Boolean(selectedBuildingId && row.inherited));
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingTemplate(null);
    setSaveAsBuildingOverride(false);
  };

  const handlePreviewTemplate = async (template: PdfTemplate) => {
    try {
      const blob = await pdfTemplatesApi.previewTemplate(template.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error: any) {
      console.error('Error generating preview:', error);
      toast.error(error.response?.data?.message || 'Failed to generate preview');
    }
  };

  const selectedOperator = operators.find((o) => o.id === selectedOperatorScopeId);
  const scopeLabel = selectedBuildingId
    ? selectedBuilding?.name ?? t('building')
    : scopeOperatorId && selectedOperator
      ? `Mẫu chủ đầu tư — ${selectedOperator.name}`
      : user?.role === 'SUPERADMIN'
        ? 'Mẫu hệ thống (toàn cục)'
        : 'Mẫu chủ đầu tư (mặc định cho các chung cư)';

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('pdfTemplateManagement')}
        </h1>
        <p className="text-gray-600">
          Quản lý mẫu PDF theo chủ đầu tư hoặc từng chung cư. Chung cư chưa chỉnh sửa sẽ dùng mẫu
          chủ đầu tư.
        </p>
      </div>

      <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Chọn chung cư
        </label>
        {shouldShowBuildingSelector(buildings) ? (
          <select
            value={selectedBuildingId}
            onChange={(e) => {
              selectBuilding(user?.id, e.target.value, setSelectedBuildingId);
            }}
            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            disabled={!needsBuildingSelection(buildings) && buildings.length === 0}
          >
            <option value="">— Mẫu chủ đầu tư (mặc định) —</option>
            {buildings.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-gray-600">{scopeLabel}</p>
        )}
        <p className="mt-2 text-xs text-gray-500">
          {selectedBuildingId
            ? 'Chỉnh sửa tạo mẫu riêng cho chung cư này. Bấm «Dùng mẫu CĐT» để bỏ override.'
            : 'Mẫu áp dụng cho mọi chung cư chưa có mẫu riêng.'}
        </p>
        {user?.role === 'SUPERADMIN' && !selectedBuildingId && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phạm vi chủ đầu tư (superadmin)
            </label>
            <select
              value={selectedOperatorScopeId}
              onChange={(e) => setSelectedOperatorScopeId(e.target.value)}
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Hệ thống (mẫu toàn cục)</option>
              {operators.map((operator) => (
                <option key={operator.id} value={operator.id}>
                  {operator.name} ({operator.email})
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500">
              Chọn chủ đầu tư để chỉnh «Mẫu chủ đầu tư» của họ mà không cần đăng nhập tài khoản
              điều hành.
            </p>
          </div>
        )}
      </div>

      {!selectedBuildingId && (
        <div className="mb-6 flex flex-wrap gap-4">
          <button
            type="button"
            onClick={() => {
              setEditingTemplate(null);
              setSaveAsBuildingOverride(false);
              setShowAddModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('addPdfTemplate')}
          </button>
          {(user?.role !== 'SUPERADMIN' || !selectedOperatorScopeId) && (
            <button
              type="button"
              onClick={handleSeedDefaults}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              {t('createDefaultTemplates')}
            </button>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-3 border-b border-gray-100 text-sm font-medium text-gray-700">
          Phạm vi: {scopeLabel}
        </div>
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-2 text-gray-500">{t('loading')}</p>
          </div>
        ) : templateRows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {selectedBuildingId
              ? 'Chưa có mẫu chủ đầu tư. Tạo mẫu mặc định ở phạm vi «Mẫu chủ đầu tư» trước.'
              : t('noTemplatesYet')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('templateName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('templateType')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nguồn
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {templateRows.map((row) => {
                  const template = row.template;
                  return (
                    <tr key={`${template.id}-${row.inherited ? 'inherited' : 'own'}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {template.name}
                            {template.isDefault && !row.inherited && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {t('defaultTemplate')}
                              </span>
                            )}
                          </div>
                          {template.description && (
                            <div className="text-sm text-gray-500">{template.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getPdfTemplateTypeLabel(template.templateType, t)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            template.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {template.isActive ? t('active') : t('inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getTemplateSourceLabel({
                          template,
                          inherited: row.inherited,
                          buildingSelected: Boolean(selectedBuildingId),
                          scopeOperatorId,
                          scopeOperatorName: selectedOperator?.name,
                          globalLabel: t('globalTemplate'),
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handlePreviewTemplate(template)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Preview
                          </button>
                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {t('edit')}
                          </button>
                          {!selectedBuildingId && !template.isDefault && (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(template)}
                              className="text-amber-600 hover:text-amber-900"
                            >
                              {t('setAsDefault')}
                            </button>
                          )}
                          {selectedBuildingId && !row.inherited && (
                            <button
                              type="button"
                              onClick={() => handleResetBuildingTemplate(row)}
                              className="text-amber-600 hover:text-amber-900"
                            >
                              Dùng mẫu CĐT
                            </button>
                          )}
                          {!selectedBuildingId && (
                            <button
                              type="button"
                              onClick={() => handleDeleteTemplate(row)}
                              className="text-red-600 hover:text-red-900"
                            >
                              {t('delete')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <TemplateEditor
        template={editingTemplate}
        isOpen={showAddModal}
        onClose={closeModal}
        onSave={() => {
          loadTemplates();
          closeModal();
        }}
        buildingContextId={selectedBuildingId || null}
        saveAsBuildingOverride={saveAsBuildingOverride}
        operatorScopeId={scopeOperatorId}
      />
    </div>
  );
};

export default PdfTemplates;
